import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import './lp-1.scss';
import centerImagePath from './images/lpBannerCenter.png';
import centerImageMobilePath from './images/lpBannerCenterMobile.png';
import girlImage from './images/girlImage.png';
import Footer from '../../components/common/Footer';
import {
  extractRecommendationJwt,
  fetchRecommendation,
  isRecommendationDobVerified,
  markRecommendationDobVerified,
  startRecommendationCheckout,
  unlockRecommendation,
  RecommendationPayload,
} from '../../api/gsRecommendations';
import HeaderLp from './HeaderLp';

const emptyRecommendation: RecommendationPayload = {
  first_name: '',
  surname: '',
  plan_reference: '',
  gp_full_name: '',
  gp_first_name: '',
  finding_title: '',
  finding_context: '',
  clinical_note: '',
  service_name: '',
  location: '',
  standard_price: '',
  member_price: '',
  clinical_rationale: '',
  calendar_link: 'https://calendar.app.google/zFr1dWR5oZ4XmsYs8',
  aftercare_first_name: '',
  medical_director: 'Dr Nitin Shori',
  stripe_link: '',
  paid: false,
};

const formatDobInput = (raw: string) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidDobFormat = (value: string) => /^\d{2}\/\d{2}\/\d{4}$/.test(String(value || '').trim());

const priceLabel = (value: number | string | undefined) =>
  value === 0 || value ? String(value) : '—';

const Lp1: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsDob, setNeedsDob] = useState(false);
  const [dob, setDob] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [token, setToken] = useState(() => extractRecommendationJwt(searchParams.get('token')));
  const [data, setData] = useState<RecommendationPayload>(emptyRecommendation);

  const applyRecommendation = useCallback((payload: RecommendationPayload) => {
    setData({ ...emptyRecommendation, ...payload });
    setNeedsDob(false);
    setError(null);
  }, []);

  const loadRecommendation = useCallback(
    async (jwt: string) => {
      if (!jwt) {
        setLoading(false);
        setError(
          'This page requires a valid private link. Please use the link from your email, or contact us for a new one.'
        );
        return;
      }

      setLoading(true);
      setError(null);

      if (!isRecommendationDobVerified(jwt)) {
        setNeedsDob(true);
        setError('Enter your date of birth to view your personalised plan.');
        setLoading(false);
        return;
      }

      const { ok, status, body } = await fetchRecommendation(jwt);

      if (ok && body?.success && body.data) {
        applyRecommendation(body.data);
        setLoading(false);
        return;
      }

      if (body?.error_code === 'dob_required' || status === 410 || body?.error_code === 'link_expired') {
        setNeedsDob(true);
        setError(body?.error || 'Enter your date of birth to view your personalised plan.');
        setLoading(false);
        return;
      }

      setError(
        body?.error ||
          'This link is not valid. Please check the URL or contact us for a new link.'
      );
      setLoading(false);
    },
    [applyRecommendation]
  );

  useEffect(() => {
    const jwt = extractRecommendationJwt(searchParams.get('token'));
    setToken(jwt);
    loadRecommendation(jwt);
  }, [searchParams, loadRecommendation]);

  const handleUnlock = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;

    const dobValue = dob.trim();
    if (!isValidDobFormat(dobValue)) {
      setUnlockError('Please enter your date of birth in DD/MM/YYYY format.');
      return;
    }

    setUnlockLoading(true);
    setUnlockError(null);
    const { ok, body } = await unlockRecommendation(token, dobValue);
    setUnlockLoading(false);

    if (ok && body?.success && body.data) {
      const verifiedToken = body.access_token || token;
      markRecommendationDobVerified(verifiedToken, body.data.uuid);
      if (body.access_token) {
        const next = new URLSearchParams(searchParams);
        next.set('token', body.access_token);
        setSearchParams(next, { replace: true });
        setToken(body.access_token);
      }
      applyRecommendation(body.data);
      return;
    }

    setUnlockError(body?.error || 'Unable to verify date of birth.');
  };

  const handleBookNow = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (data.paid) {
      window.location.href = `/personalised-plan/payment-successful?uuid=${encodeURIComponent(data.uuid || '')}`;
      return;
    }

    if (!token) {
      setError('Missing access token. Please reopen your private link.');
      return;
    }

    setCheckoutLoading(true);
    const { ok, body } = await startRecommendationCheckout(token, { landing: 'corevitals' });
    setCheckoutLoading(false);

    if (ok && body?.data?.already_paid && body.data.redirect_url) {
      window.location.href = body.data.redirect_url;
      return;
    }

    if (ok && body?.data?.checkout_url) {
      window.location.href = body.data.checkout_url;
      return;
    }

    setError(body?.error || 'Unable to start payment. Please try again.');
  };

  if (loading) {
    return (
      <main >
        <div className='jakarta-font lp-state intro-content'>
          <HeaderLp />
        <p>Loading your plan…</p>
        </div>
        <Footer />
      </main>
    );
  }

  if (error && !needsDob && !data.finding_title) {
    return (
      <main className="">
        
          <HeaderLp />
          <div className='jakarta-font lp-state intro-content'>
        <h1 className="lp-dark-text">Unable to open this page</h1>
        <p>{error}</p>
        <p>
          Need help? Email <a href="mailto:care@getscanned.me">care@getscanned.me</a> or call 020
          4592 0454.
        </p>
        </div>
        <Footer />
      </main>
    );
  }

  if (needsDob) {
    return (
      <main className="">
        
        <HeaderLp />
        <div className='jakarta-font lp-state intro-content'>
        
        <h1 className="lp-dark-text">{error || 'Enter your date of birth (DD/MM/YYYY) to view your personalised plan.'}</h1>
        <form className="lp-unlock-form" onSubmit={handleUnlock}>
          <input
            type="text"
            placeholder="DD/MM/YYYY"
            inputMode="numeric"
            maxLength={10}
            autoComplete="bday"
            value={dob}
            onChange={(e) => setDob(formatDobInput(e.target.value))}
            required
          />
          {unlockError && <p className="lp-error">{unlockError}</p>}
          <button type="submit" className="lp-button lp-primary-button" disabled={unlockLoading}>
            {unlockLoading ? 'Checking…' : 'Continue'}
          </button>
        </form>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <div className="jakarta-font">
        <HeaderLp />

        {error && <p className="lp-error lp-inline-error">{error}</p>}

        <section className="box-pad intro-section">
          <div className="lp-tag">
            <span className="lp-tag-icon"></span>
            YOUR FIRST ACTION
          </div>

          <div className="intro-content">
            <h1 className="lp-dark-text">Hi {data.first_name || 'there'}, start here.</h1>
            <p className="lp-dark-text">
              Your results have been reviewed and your plan is written. This is the first action
              from it.
            </p>
          </div>
        </section>

        <section className="gray-block priority-section">
          <div className="lp-tag">
            <span className="lp-tag-icon"></span>
            HIGHEST PRIORITY
          </div>

          <div className="priority-content">
            <h2 className="lp-dark-text">{data.finding_title}</h2>
            <p className="lp-dark-text">{data.finding_context}</p>
          </div>

          <div className="lp-banner">
            <div className="lp-banner-copy">
              <h3>Reading your results changes nothing. Acting on them is the point.</h3>
              <p>
                Most people never start because they&apos;re waiting to do everything at once. You
                don&apos;t have to - start with this one. We&apos;ll handle the order.
              </p>
            </div>

            <div>
              <img alt="" className="lp-banner-image desktop-banner-image" src={centerImagePath} />
              <img
                alt=""
                className="lp-banner-image mobile-banner-image"
                src={centerImageMobilePath}
              />
            </div>
          </div>
        </section>

        <section className="box-pad gp-note">
          <p className="gp-label">A note from {data.gp_full_name || 'your clinician'}</p>
          <p className="gp-note-text">{data.clinical_note}</p>
          <p className="gp-signature">
            - {data.gp_full_name || data.gp_first_name || 'your clinician'}, CoreVitals
          </p>
        </section>

        <section className="box-pad gray-block next-step-section">
          <div className="lp-tag">
            <span className="lp-tag-icon"></span>
            RECOMMENDED NEXT STEP
          </div>

          <div className="service-content">
            <h2 className="lp-dark-text">{data.service_name}</h2>

            <div className="location">
              <span>{data.location}</span>
            </div>

            <div className="price-row">
              <div className="prices">
                <span className="standard-price">£{priceLabel(data.standard_price)}</span>
                <span className="lp-dark-text">£{priceLabel(data.member_price)}</span>
              </div>

              <div className="member-price">Member Price</div>
            </div>
          </div>

          <div className="booking-content">
            {data.paid ? (
              <div className="paid-notice">
                <h3 className="lp-dark-text">Payment is already done</h3>
                <p>
                  We&apos;ll let you know if any further step is needed from your end.
                  {data.aftercare_first_name
                    ? ` ${data.aftercare_first_name} may also be in touch if anything else is required.`
                    : ''}
                </p>
                <button
                  type="button"
                  className="lp-button lp-secondary-button"
                  onClick={() => setDetailsOpen(true)}
                >
                  View details
                </button>
              </div>
            ) : (
              <>
                <div className="button-row">
                  <button
                    type="button"
                    className="lp-button lp-primary-button"
                    onClick={handleBookNow}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading
                      ? 'Starting checkout…'
                      : `Book now £${priceLabel(data.member_price)}`}
                  </button>
                  <button
                    type="button"
                    className="lp-button lp-secondary-button"
                    onClick={() => setDetailsOpen(true)}
                  >
                    View details
                  </button>
                </div>

                <p className="booking-note">
                  After booking, {data.aftercare_first_name || 'your aftercare manager'} will call
                  you to arrange a time.
                </p>
              </>
            )}
          </div>
        </section>

        <section className="box-pad aftercare-section">
          <div className="aftercare-content">
            <div className="aftercare-intro">
              <div className="manager-image">
                <img alt="" className="manager-image" src={girlImage} />
              </div>

              <h2 className="lp-dark-text">
                Meet {data.aftercare_first_name || 'your aftercare manager'},
                <br />
                <span>your patient experience manager.</span>
              </h2>

              
              <p>From here, {data.aftercare_first_name || 'Your aftercare manager'} looks after you personally.</p>
              <p>She's a doctor by background, and familiar with your plan, so she isn't starting from scratch when you reach out.</p> 
              <p>She'll book your scans, chase your results, and answer your questions along the way.</p>
              
            

            </div>

            <p className="aftercare-description">
              Want to talk it through first? Book a call here.
            </p>

            <div>
              <a
                href={data.calendar_link || 'https://calendar.app.google/zFr1dWR5oZ4XmsYs8'}
                className="lp-button lp-primary-button"
                target="_blank"
                rel="noreferrer"
              >
                Book a call
              </a>
            </div>
          </div>

          <p className="plan-details">
            {[data.first_name, data.surname].filter(Boolean).join(' ')}
            <br />
            {data.plan_reference}
            <br />
            {data.medical_director || 'Dr Nitin Shori'}
          </p>

          <p className="privacy-note">
            This page is private to you. It contains your health information.
            <span className="lp-dark-text">Please don&apos;t share the link.</span>
            <br className="desktop-only" />
            Your plan is based on the results available at the time of review. It does not replace
            ongoing care from your GP.
          </p>
        </section>
      </div>

      {detailsOpen && (
        <div className="lp-details-modal" role="dialog" aria-modal="true">
          <div className="lp-details-card">
            <button
              type="button"
              className="lp-details-close"
              onClick={() => setDetailsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Why this test?</h3>
            <p>{data.clinical_rationale || 'Details will be shared by your care team.'}</p>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Lp1;
